"use client";

import { apiGetHomePopupList, type HomePopup } from "@/api/home";
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog";
import { processImageUrl } from "@/components/ui/smart-image-utils";
import { useAppVersionStore } from "@/store/app-version";
import { useAppRouter } from "@/utils/router";
import { memo, useCallback, useEffect, useRef, useState } from "react";

type HomePopupSessionState = { status: "running" | "finished"; queue: HomePopup[]; index: number };

declare global {
    interface Window {
        homePopupSessionState?: HomePopupSessionState;
        homePopupShownInSession?: boolean;
    }
}

function getHomePopupRouteUrl( jumpUrl: string ) {
    if ( jumpUrl.startsWith( "/" ) ) {
        return jumpUrl;
    }

    try {
        const parsedUrl = new URL( jumpUrl );
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
        return `/${jumpUrl}`;
    }
}

function HomeImagePopup() {
    const router = useAppRouter();
    const showVersionPopup = useAppVersionStore( state => state.showVersionPopup );
    const versionCheckFinished = useAppVersionStore( state => state.versionCheckFinished );
    const [ homePopupQueue, setHomePopupQueue ] = useState<HomePopup[]>( [] );
    const [ homePopupIndex, setHomePopupIndex ] = useState( 0 );
    const [ showPopup, setShowPopup ] = useState( false );
    const [ homePopupFlowFinished, setHomePopupFlowFinished ] = useState( false );
    const homePopupFlowStartedRef = useRef( false );
    const homePopupFlowFinishedRef = useRef( false );
    const homePopupAdvanceTimerRef = useRef<number | null>( null );

    const currentPopup = homePopupQueue[homePopupIndex] ?? null;
    const open = showPopup && !showVersionPopup && !homePopupFlowFinished;

    const updateHomePopupSessionState = useCallback( ( state: HomePopupSessionState ) => {
        window.homePopupSessionState = state;
        window.homePopupShownInSession = state.status === "finished";
    }, [] );

    const finishHomePopupFlow = useCallback( () => {
        if ( homePopupFlowFinishedRef.current ) return;

        homePopupFlowFinishedRef.current = true;
        homePopupFlowStartedRef.current = true;
        if ( homePopupAdvanceTimerRef.current !== null ) {
            window.clearTimeout( homePopupAdvanceTimerRef.current );
            homePopupAdvanceTimerRef.current = null;
        }
        setShowPopup( false );
        setHomePopupQueue( [] );
        setHomePopupIndex( 0 );
        setHomePopupFlowFinished( true );
        updateHomePopupSessionState( { status: "finished", queue: [], index: 0 } );
    }, [ updateHomePopupSessionState ] );

    const openNextHomePopup = useCallback( ( nextIndex: number ) => {
        if ( homePopupFlowFinishedRef.current ) return;

        setShowPopup( false );
        if ( homePopupAdvanceTimerRef.current !== null ) {
            window.clearTimeout( homePopupAdvanceTimerRef.current );
        }

        updateHomePopupSessionState( { status: "running", queue: homePopupQueue, index: nextIndex } );

        homePopupAdvanceTimerRef.current = window.setTimeout( () => {
            homePopupAdvanceTimerRef.current = null;
            setHomePopupIndex( nextIndex );
            setShowPopup( true );
        }, 0 );
    }, [ homePopupQueue, updateHomePopupSessionState ] );

    const closeCurrentHomePopup = useCallback( () => {
        if ( homePopupFlowFinishedRef.current ) return;

        const nextIndex = homePopupIndex + 1;
        if ( nextIndex >= homePopupQueue.length ) {
            finishHomePopupFlow();
            return;
        }

        updateHomePopupSessionState( { status: "running", queue: homePopupQueue, index: nextIndex } );
        openNextHomePopup( nextIndex );
    }, [ finishHomePopupFlow, homePopupIndex, homePopupQueue, openNextHomePopup, updateHomePopupSessionState ] );

    const jumpCurrentHomePopup = useCallback( () => {
        const jumpUrl = currentPopup?.jumpUrl?.trim();
        const jumpType = currentPopup?.jumpType;

        finishHomePopupFlow();
        if ( !jumpUrl ) {
            return;
        }

        if ( jumpType === "h5" ) {
            window.location.href = jumpUrl;
            return;
        }

        if ( jumpType === "route" ) {
            void router.push( getHomePopupRouteUrl( jumpUrl ) );
        }
    }, [ currentPopup, finishHomePopupFlow, router ] );

    const handleOpenChange = useCallback( ( nextOpen: boolean ) => {
        if ( !nextOpen ) {
            closeCurrentHomePopup();
        }
    }, [ closeCurrentHomePopup ] );

    const handleImageClick = useCallback( () => {
        if ( !currentPopup ) return;

        if ( currentPopup.jumpType === "route" || currentPopup.jumpType === "h5" ) {
            jumpCurrentHomePopup();
            return;
        }

        closeCurrentHomePopup();
    }, [ closeCurrentHomePopup, currentPopup, jumpCurrentHomePopup ] );

    useEffect( () => {
        if ( !versionCheckFinished || showVersionPopup ) return;
        if ( homePopupFlowStartedRef.current || homePopupFlowFinishedRef.current ) return;

        const sessionState = window.homePopupSessionState;
        if ( sessionState?.status === "running" && sessionState.queue.length > 0 ) {
            homePopupFlowStartedRef.current = true;
            homePopupFlowFinishedRef.current = false;
            setHomePopupQueue( sessionState.queue );
            setHomePopupIndex( Math.min( sessionState.index, sessionState.queue.length - 1 ) );
            setShowPopup( true );
            return;
        }

        if ( window.homePopupShownInSession ) {
            homePopupFlowStartedRef.current = true;
            homePopupFlowFinishedRef.current = true;
            setHomePopupFlowFinished( true );
            return;
        }

        homePopupFlowStartedRef.current = true;
        let cancelled = false;

        const loadHomePopups = async () => {
            try {
                const list = await apiGetHomePopupList();
                if ( cancelled ) return;

                if ( !list.length ) {
                    finishHomePopupFlow();
                    return;
                }

                updateHomePopupSessionState( { status: "running", queue: list, index: 0 } );
                setHomePopupQueue( list );
                setHomePopupIndex( 0 );
                setShowPopup( true );
            } catch {
                if ( !cancelled ) {
                    finishHomePopupFlow();
                }
            }
        };

        void loadHomePopups();

        return () => {
            cancelled = true;
        };
    }, [ finishHomePopupFlow, showVersionPopup, updateHomePopupSessionState, versionCheckFinished ] );

    useEffect( () => {
        return () => {
            if ( homePopupAdvanceTimerRef.current !== null ) {
                window.clearTimeout( homePopupAdvanceTimerRef.current );
                homePopupAdvanceTimerRef.current = null;
            }
        };
    }, [] );

    useEffect( () => {
        if ( !open ) return;

        if ( currentPopup && !currentPopup.imageUrl.trim() ) {
            closeCurrentHomePopup();
        }
    }, [ closeCurrentHomePopup, currentPopup, open ] );

    if ( !currentPopup ) return null;

    return (
        <Dialog
            key={currentPopup.id || "home-popup-empty"}
            open={open}
            onOpenChange={handleOpenChange}
            enableBackInterceptor={false}
        >
            <DialogPortal>
                <DialogOverlay className="bg-overlay-strong backdrop-blur-[4px]" />
                <DialogContent
                    className="w-[320px] h-[484px] p-0 shadow-none"
                    showCloseButton={false}
                    title=""
                    onInteractOutside={( e ) => e.preventDefault()}
                    onEscapeKeyDown={( e ) => e.preventDefault()}
                >
                    <DialogTitle className="sr-only">popup</DialogTitle>

                    <div className="flex w-full h-full flex-col items-center gap-6">
                        <img
                            src={processImageUrl( currentPopup.imageUrl )}
                            alt="Home popup"
                            width={320}
                            height={428}
                            className="w-full cursor-pointer rounded-xl transition-opacity hover:opacity-90"
                            onClick={handleImageClick}
                            onError={closeCurrentHomePopup}
                        />

                        <button
                            onClick={closeCurrentHomePopup}
                            className="flex h-8 w-8 items-center justify-center rounded-full border-[1.5px] border-content-fixed-inverse bg-overlay transition-opacity hover:opacity-80"
                            aria-label="close popup"
                            type="button"
                        >
                            <svg
                                width="10"
                                height="10"
                                viewBox="0 0 10 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M9 1L1 9M1 1L9 9" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}

export default memo( HomeImagePopup );
